import { useMemo } from "react";
import { Checkbox } from "components/forms/fragments/Checkbox";
import TableBody from "./TableBody";
import TableColumnHeader from "./TableColumnHeader";
import TableHeader from "./TableHeader";
import TableFooter from "./TableFooter";
import { BulkTableAction, TableConfig } from "./types";
import { useTableLogic } from "./useTableLogic";
import { useTableState } from "./useTableState";

const GenericTable = <T extends Record<string, any>>({ config }: { config: TableConfig<T> }) => {
  const {
    columns,
    data,
    title,
    actions,
    selectable = false,
    serverSort,
    toolbarExtra,
    columnReorder,
    centerActions = false,
    expandable,
    pagination = { enabled: false, pageSize: 10 },
    filters = { enabled: false },
    loading = false,
    skeletonRowCount = 5,
    onFilter,
    onRowClick,
    getRowClassName,
    variant = 'default',
    styling = {}
  } = config;

  const isSoft = variant === 'soft';

  const { state, updateState } = useTableState<T>();
  const { sortedData } = useTableLogic(data, columns, state);

  const paginatedData = useMemo(() => {
    if (!pagination.enabled) return sortedData;

    const startIndex = (state.currentPage - 1) * (pagination.pageSize || 10);
    const endIndex = startIndex + (pagination.pageSize || 10);
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, state.currentPage, pagination]);

  const totalPages = pagination.totalPages || 8;

  // Tri serveur : colonne affichée comme triée = celle dont le sortKey est actif
  const serverSortColumnKey = serverSort
    ? columns.find((col) => col.sortKey && col.sortKey === serverSort.field)?.key ?? null
    : null;

  const handleSort = (columnKey: Extract<keyof T, string>) => {
    if (loading) return; // Empêcher le tri pendant le loading

    const column = columns.find(col => col.key === columnKey);
    if (!column?.sortable) return;

    // Mode tri serveur : on délègue (le back trie, la page repart à 1)
    if (serverSort) {
      if (column.sortKey) serverSort.onSort(column.sortKey);
      return;
    }

    if (state.sortColumn === columnKey) {
      const newDirection = state.sortDirection === 'asc' ? 'desc' : state.sortDirection === 'desc' ? null : 'asc';
      updateState({
        sortDirection: newDirection,
        sortColumn: newDirection ? columnKey : null
      });
    } else {
      updateState({
        sortColumn: columnKey,
        sortDirection: 'asc'
      });
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (loading) return; // Empêcher la sélection pendant le loading
    
    const newSelection = checked
      ? new Set(paginatedData.map((_, index) => index))
      : new Set<number>();
    updateState({ selectedRows: newSelection });
  };

  const handleSelectRow = (index: number, checked: boolean) => {
    if (loading) return; // Empêcher la sélection pendant le loading
    
    const newSelection = new Set(state.selectedRows);
    if (checked) {
      newSelection.add(index);
    } else {
      newSelection.delete(index);
    }
    updateState({ selectedRows: newSelection });
  };

  const handleBulkAction = (action: BulkTableAction<T>) => {
    if (loading) return; // Empêcher les actions pendant le loading
    
    const selectedData = Array.from(state.selectedRows).map(i => paginatedData[i]);
    const selectedIndices = Array.from(state.selectedRows);
    action.onClick(selectedData, selectedIndices);
  };

  return (
    <div className={`${styling.containerClass || ''}`}>
      <TableHeader
        title={title}
        actions={actions}
        filters={filters}
        toolbarExtra={toolbarExtra}
        onFilter={onFilter}
        selectedFiltersByGroup={state.selectedFiltersByGroup}
      />
      <div className={isSoft ? '' : 'overflow-hidden rounded-xl mt-3 shadow-lg'}>
        {/* Chips des filtres actifs : bandeau accroché au tableau, la barre d'outils reste collée */}
        {filters?.renderActiveFilters?.()}
        <div className="overflow-x-auto">
          <table className={`w-full ${styling.tableClass || ''}`}>
            <thead className={`${isSoft ? 'bg-primary/5 border-b border-primary/10' : 'bg-primary'} ${styling.headerClass || ''}`}>
              <tr>
                {selectable && (
                  <th className="px-6 py-auto text-left">
                    <Checkbox
                      checked={
                        !loading &&
                        state.selectedRows.size === paginatedData.length &&
                        paginatedData.length > 0
                      }
                      disabled={loading}
                      onChange={(checked) => handleSelectAll(checked)}
                    />
                  </th>
                )}
                {expandable && <th className="w-8" />}
                {columns.map((column) => (
                  <TableColumnHeader
                    key={column.key as string}
                    column={column}
                    variant={variant}
                    reorderable={!!columnReorder}
                    onColumnDrop={columnReorder?.onMove}
                    sortColumn={serverSort ? serverSortColumnKey : state.sortColumn}
                    sortDirection={serverSort ? serverSort.direction : state.sortDirection}
                    columnFilters={state.columnFilters}
                    onSort={handleSort}
                    onFilter={(columnKey, value) =>
                      !loading && updateState({
                        columnFilters: { ...state.columnFilters, [columnKey]: value }
                      })
                    }
                  />
                ))}
                {actions?.row && (
                  <th className={`px-6 py-3 tracking-wider ${
                    centerActions ? 'text-center' : isSoft ? 'text-right' : 'text-left'
                  } ${isSoft ? 'text-[11px] font-semibold uppercase text-primary/80' : 'text-xs font-medium text-white'}`}>
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <TableBody
              columns={columns}
              data={paginatedData}
              selectable={selectable}
              selectedRows={state.selectedRows}
              actions={actions?.row}
              styling={styling}
              isLoading={loading}
              skeletonRowCount={skeletonRowCount}
              centerActions={centerActions}
              expandable={expandable}
              onSelectRow={handleSelectRow}
              onRowClick={onRowClick}
              getRowClassName={getRowClassName}
            />
          </table>
        </div>
        {(pagination.enabled || (!!actions?.bulk && state.selectedRows.size > 0)) && (
          <TableFooter
            currentPage={pagination.currentPage || state.currentPage}
            totalPages={pagination.totalPages || totalPages}
            totalItems={pagination.totalItems || sortedData.length}
            pageSize={pagination.pageSize || 10}
            showInfo={pagination.showInfo || false}
            onPageChange={pagination.onPageChange || ((page) => updateState({ currentPage: page }))}
            pageSizeOptions={pagination.pageSizeOptions}
            onPageSizeChange={pagination.onPageSizeChange}
            paginationEnabled={pagination.enabled}
            selectedCount={state.selectedRows.size}
            bulkActions={actions?.bulk}
            onBulkAction={handleBulkAction}
          />
        )}
      </div>
    </div>
  );
};

export default GenericTable;
